import api from './api';

// All calls route through the shared `api` axios instance (baseURL = REACT_APP_API_URL
// + '/admin'), so the 401-refresh interceptor applies — expired tokens are refreshed
// and the request retried instead of silently failing.

export interface TechnicianSchedule {
  id?: number;
  user_id: number;
  day_of_week: number;
  start_time: string;
  end_time: string;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface ScheduleException {
  id?: number;
  user_id: number;
  exception_type: 'time_off' | 'break' | 'special_hours' | 'holiday';
  start_datetime: string;
  end_datetime: string;
  reason?: string;
  is_recurring: boolean;
  recurrence_pattern?: {
    frequency: 'weekly' | 'monthly';
    interval: number;
  };
  // WF-1 Task 4: shop-wide (holiday) blocks fan out to one row per tech, all
  // sharing this TEXT group_id. Present on shop-wide rows, undefined on
  // single-tech blocks. Task 8 uses it to edit the whole group + label scope.
  group_id?: string;
  shop_id?: number;
  // WF-1 Task 8: the owning user's user_profiles.calendar_color, used to tint
  // the availability block on the calendar. May be absent (the list endpoint
  // does not always join it) — callers fall back to a deterministic swatch.
  calendar_color?: string;
  created_at?: string;
  updated_at?: string;
}

// WF-1 Task 7: write payload for POST/PUT /schedules/exceptions. Distinct
// from ScheduleException (the read/list shape, which always carries a real
// user_id once persisted) because a create/update call can be scope='shop'
// (no user_id — the server fans it out to every active tech) or scope='tech'
// (single row, user_id required). scope defaults to 'tech' server-side
// (backend/src/routes/schedules.ts exceptionSchema), so it's optional here
// too — existing callers that never sent scope keep working unchanged.
export interface ExceptionWritePayload {
  scope?: 'tech' | 'shop';
  user_id?: number;
  exception_type: 'time_off' | 'break' | 'special_hours' | 'holiday';
  start_datetime: string;
  end_datetime: string;
  reason?: string;
  is_recurring: boolean;
  recurrence_pattern?: {
    frequency: 'weekly';
    interval: number;
  };
}

export interface BookingSettings {
  id?: number;
  allow_customer_booking: boolean;
  max_daily_appointments: number;
  buffer_time_minutes: number;
  booking_advance_days_min: number;
  booking_advance_days_max: number;
  allow_diagnosis_booking: boolean;
  allow_repair_booking: boolean;
  allow_inspection_booking: boolean;
  default_diagnosis_duration_minutes: number;
  default_repair_duration_minutes: number;
  default_inspection_duration_minutes: number;
  auto_assign_technician: boolean;
  prefer_nearest_technician: boolean;
  balance_workload: boolean;
  send_confirmation_email: boolean;
  send_confirmation_sms: boolean;
  send_reminder_email: boolean;
  send_reminder_sms: boolean;
  reminder_hours_before: number;
  custom_booking_hours: boolean;
  booking_start_time?: string;
  booking_end_time?: string;
  allow_multi_day_booking: boolean;
  availability_blocks_enabled: boolean;
  created_at?: string;
  updated_at?: string;
}

export const scheduleApi = {
  // Technician Schedules
  getAllTechniciansWithSchedules: async () => {
    const response = await api.get('/schedules/technicians');
    return response.data;
  },

  getTechnicianSchedule: async (userId: number): Promise<TechnicianSchedule[]> => {
    const response = await api.get(`/schedules/technicians/${userId}`);
    return response.data;
  },

  updateTechnicianSchedule: async (userId: number, schedules: Omit<TechnicianSchedule, 'id' | 'user_id' | 'created_at' | 'updated_at'>[]): Promise<TechnicianSchedule[]> => {
    const response = await api.put(`/schedules/technicians/${userId}`, schedules);
    return response.data;
  },

  deleteTechnicianSchedule: async (userId: number): Promise<void> => {
    await api.delete(`/schedules/technicians/${userId}`);
  },

  // Schedule Exceptions
  getExceptions: async (params: { user_id?: number; start_date?: string; end_date?: string }): Promise<ScheduleException[]> => {
    const response = await api.get('/schedules/exceptions', { params });
    return response.data;
  },

  createException: async (exception: ExceptionWritePayload): Promise<ScheduleException | ScheduleException[]> => {
    // scope='shop' fans out to every active tech and returns an array; the
    // default scope='tech' returns the single created row.
    const response = await api.post('/schedules/exceptions', exception);
    return response.data;
  },

  updateException: async (id: number, exception: Partial<ExceptionWritePayload>): Promise<ScheduleException> => {
    const response = await api.put(`/schedules/exceptions/${id}`, exception);
    return response.data;
  },

  deleteException: async (id: number): Promise<void> => {
    await api.delete(`/schedules/exceptions/${id}`);
  },

  // Booking Settings
  getBookingSettings: async (): Promise<BookingSettings> => {
    const response = await api.get('/schedules/booking-settings');
    return response.data;
  },

  updateBookingSettings: async (settings: Partial<BookingSettings>): Promise<BookingSettings> => {
    const response = await api.put('/schedules/booking-settings', settings);
    return response.data;
  }
};
